import posthog from "posthog-js";

function stripQueryString(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const q = value.indexOf("?");
  return q === -1 ? value : value.slice(0, q);
}

let initialized = false;

export function initPostHog(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return;

  const env = import.meta.env.VITE_ENV;
  if (env !== "production") return;

  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || "https://m.sanctions.io",
    ui_host: import.meta.env.VITE_POSTHOG_UI_HOST || "https://eu.posthog.com",
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    person_profiles: "identified_only",
    sanitize_properties(properties) {
      const cleaned: Record<string, unknown> = { ...(properties ?? {}) };
      for (const prop of ["$current_url", "$referrer", "$pathname"]) {
        if (prop in cleaned) cleaned[prop] = stripQueryString(cleaned[prop]);
      }
      return cleaned;
    },
  });

  posthog.register({
    env: env,
  });

  initialized = true;
}

/** Associate events with the backend user id (uuid) plus safe traits. */
export function identifyUser(userId: string, traits?: Record<string, unknown>): void {
  if (!initialized || !userId) return;
  posthog.identify(userId, traits);
}

/** Disassociate on logout so the next user starts a clean profile. */
export function resetUser(): void {
  if (!initialized) return;
  posthog.reset();
}

/** Manual SPA page view. Pass a query-free pathname (never the searched name). */
export function capturePageview(pathname: string, pageName?: string): void {
  if (!initialized) return;
  posthog.capture("$pageview", {
    $current_url: window.location.origin + pathname,
    $pathname: pathname,
    ...(pageName ? { page_name: pageName } : {}),
  });
}

/**
 * Associate all subsequent events in this session with a B2B company group.
 * Call once per login, right after identifyUser(), so every event is attributed
 * to the company in PostHog's group analytics.
 */
export function groupCompany(
  companyId: string | number,
  properties?: Record<string, unknown>,
): void {
  if (!initialized) return;
  posthog.group("company", String(companyId), properties);
}

/** Explicit CTA event. Properties MUST be PII-free (see the spec taxonomy). */
export function captureEvent(event: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.capture(event, properties);
}

